import React from 'react';
import { ListChecks } from 'lucide-react';
import { Section, SegmentedControl } from '../ui';
import { FreeTierHint } from './FreeTierHint';
import type { SubscriptionFilters } from '../SubscriptionEditorFields';

export interface FeaturesGroupProps {
  density?: 'default' | 'compact';
  filters: SubscriptionFilters;
  onFilterChange: (field: string, value: string) => void;
  isLoading: boolean;
  showFreeHint: boolean;
}

interface AttrOption {
  value: string;
  label: string;
}

interface Attr {
  id: keyof SubscriptionFilters;
  label: string;
  options: AttrOption[];
  hint?: string;
}

// Values map 1:1 to the existing filter payload (ANY/ONLY/NONE, COMMERCIAL/PRIVATE) — only the labels change.
const ATTRS: Attr[] = [
  { id: 'wbsFilter', label: 'WBS', options: [{ value: 'ANY', label: 'Alle' }, { value: 'ONLY', label: 'nur mit WBS' }, { value: 'NONE', label: 'ohne WBS' }] },
  { id: 'wgFilter', label: 'WG', options: [{ value: 'ANY', label: 'Alle' }, { value: 'ONLY', label: 'nur WG' }, { value: 'NONE', label: 'keine WG' }] },
  { id: 'swapFilter', label: 'Tausch', options: [{ value: 'ANY', label: 'Alle' }, { value: 'ONLY', label: 'nur Tausch' }, { value: 'NONE', label: 'kein Tausch' }] },
  { id: 'tempFilter', label: 'Befristet', options: [{ value: 'ANY', label: 'Alle' }, { value: 'ONLY', label: 'nur befristet' }, { value: 'NONE', label: 'unbefristet' }] },
  { id: 'furnishedFilter', label: 'Möbliert', options: [{ value: 'ANY', label: 'Alle' }, { value: 'ONLY', label: 'möbliert' }, { value: 'NONE', label: 'unmöbliert' }] },
  { id: 'providerFilter', label: 'Anbieter', options: [{ value: 'ANY', label: 'Alle' }, { value: 'COMMERCIAL', label: 'gewerblich' }, { value: 'PRIVATE', label: 'privat' }] },
  {
    id: 'closedAreaFilter',
    label: 'Abo-Bereiche',
    options: [{ value: 'ANY', label: 'Alle' }, { value: 'ONLY', label: 'nur Abo-Bereiche' }, { value: 'NONE', label: 'ausblenden' }],
    hint: 'Anzeigen, die bei Immoscout24 nur für Plus-Mitglieder sichtbar sind. Standard: ausblenden.',
  },
];

/** "Merkmale" group: every tri-state filter as a full-word SegmentedControl. */
export const FeaturesGroup: React.FC<FeaturesGroupProps> = ({ density = 'default', filters, onFilterChange, isLoading, showFreeHint }) => {
  const content = (
    <>
      {showFreeHint && <FreeTierHint />}

      <div className={density === 'compact' ? 'flex flex-col gap-4' : 'grid gap-5 sm:grid-cols-2'}>
        {ATTRS.map((attr) => (
          <div key={attr.id} className="flex flex-col gap-1.5">
            <SegmentedControl
              label={attr.label}
              showLabel
              value={filters[attr.id] as string}
              onChange={(value) => onFilterChange(attr.id, value)}
              options={attr.options}
              disabled={isLoading}
              fullWidth
            />
            {attr.hint && <p className="text-sm text-muted">{attr.hint}</p>}
          </div>
        ))}
      </div>
    </>
  );

  if (density === 'compact') {
    // Native Telegram list convention: small muted uppercase section title (allowed only on /tg).
    return (
      <div className="flex flex-col gap-3">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-muted">Merkmale</h2>
        {content}
      </div>
    );
  }

  return (
    <Section title="Merkmale" icon={<ListChecks className="h-5 w-5" aria-hidden="true" />}>
      {content}
    </Section>
  );
};
